import { useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Primary heading and actions for an AppNotes management screen. */
export function PageHeader({
  actions,
  title,
}: {
  readonly actions?: ReactNode;
  readonly title: string;
}) {
  return (
    <header className="page-header">
      <div className="page-header-copy">
        <h1>{title}</h1>
      </div>
      {actions === undefined ? null : (
        <div className="page-actions">{actions}</div>
      )}
    </header>
  );
}

/** Shared framed collection surface for AppNotes console pages. */
export function ConsoleSection({
  actions,
  children,
  description,
  title,
  titleId,
}: {
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly description?: string;
  readonly title: string;
  readonly titleId: string;
}) {
  return (
    <section aria-labelledby={titleId} className="console-section">
      <header className="console-section-header">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description === undefined ? null : <p>{description}</p>}
        </div>
        {actions === undefined ? null : (
          <div className="console-section-actions">{actions}</div>
        )}
      </header>
      <div className="console-section-body">{children}</div>
    </section>
  );
}

/** Intentional loading, empty, forbidden, and unavailable screen state. */
export function StatePanel({
  action,
  description,
  title,
}: {
  readonly action?: ReactNode;
  readonly description: string;
  readonly title: string;
}) {
  return (
    <section className="state-panel">
      <h2>{title}</h2>
      <p>{description}</p>
      {action === undefined ? null : <div>{action}</div>}
    </section>
  );
}

/** Compact product status label. */
export function StatusBadge({
  children,
  tone = "neutral",
}: {
  readonly children: ReactNode;
  readonly tone?: "danger" | "neutral" | "primary";
}) {
  return (
    <Badge
      className={cn(
        tone === "primary" && "text-primary",
        tone === "danger" && "text-destructive",
      )}
      variant={tone === "danger" ? "destructive" : "secondary"}
    >
      {children}
    </Badge>
  );
}

/** Label-value metadata row that tolerates long content. */
export function MetadataRow({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label: string;
}) {
  return (
    <div className="grid gap-1 border-b py-3 last:border-b-0 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-sm">{children}</dd>
    </div>
  );
}

/** Copyable one-time credential or guest URL. */
export function SecretPanel({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }
  return (
    <section className="min-w-0 border border-primary/30 bg-primary/5 p-4">
      <p className="text-xs font-semibold tracking-widest text-primary uppercase">
        {label}
      </p>
      <code className="mt-3 block overflow-x-auto border bg-background p-3 text-xs select-text">
        {value}
      </code>
      <Button
        className="mt-3"
        onClick={() => void copy()}
        size="sm"
        type="button"
        variant="outline"
      >
        {copyState === "copied" ? "Copied" : "Copy"}
      </Button>
      <p className="mt-2 text-sm" role="status">
        {copyState === "failed"
          ? "Clipboard unavailable. Select and copy the credential above."
          : copyState === "copied"
            ? "Copied to clipboard."
            : ""}
      </p>
    </section>
  );
}
